import type { SubmissionRow } from '../api/client';
import { registryFieldsOf, registryValue, type Lookup } from './presenters';

/**
 * A message type's columns on the Messages screen, one set per form.
 *
 * With every form in one list the table can only show what they have in
 * common — who wrote, which form, one summary line. Pick a single form and
 * that summary is the wrong shape: a media desk wants outlet and deadline
 * side by side, a coordinator checking study alerts wants the study list.
 * So the chips do not just filter; they change the columns.
 *
 * Kept as data rather than JSX per form so a column is a label and a CRM key,
 * and the value still resolves through `/meta` like everywhere else. A form
 * missing from this map keeps the generic Form · About pair.
 *
 * Headers say what the answer means to the person replying ("They need",
 * "Also remove"), not the form's field name ("Need", "Other contact"), so a
 * column reads without opening the message.
 */
export interface Column {
  /** Header text. */
  label: string;
  /** Canonical CRM key read from the row's `fields`. */
  key: string;
  /** CSS grid track; `minmax(0,1fr)` when omitted. */
  track?: string;
}

export const FORM_COLUMNS: Record<string, Column[]> = {
  partnership: [
    { label: 'About', key: 'inquiry.topic', track: 'minmax(0,1.3fr)' },
    { label: 'Organization', key: 'person.org' },
  ],
  media: [
    { label: 'Outlet', key: 'media.outlet' },
    { label: 'Deadline', key: 'media.deadline' },
  ],
  fellowship: [
    { label: 'Interested in', key: 'training.track', track: 'minmax(0,1.4fr)' },
    { label: 'Institution', key: 'person.org' },
    { label: 'Timing', key: 'training.timing', track: 'minmax(0,0.8fr)' },
  ],
  // Whether they may be emailed a reply is almost always yes, so it is not a
  // column: the exception is flagged beside their name instead.
  'pre-referral-fit': [
    { label: 'Study', key: 'study.slug', track: 'minmax(0,0.8fr)' },
    { label: 'Asking', key: 'fit.kind', track: 'minmax(0,2.2fr)' },
  ],
  'clinician-alerts': [
    { label: 'Tell them when these open', key: 'interest.topics[]', track: 'minmax(0,1.8fr)' },
    { label: 'Also on close', key: 'alert.stop_on_close', track: 'minmax(110px,0.6fr)' },
  ],
  'referral-status': [
    { label: 'Study', key: 'study.slug', track: 'minmax(0,0.8fr)' },
    { label: 'They need', key: 'ping.need', track: 'minmax(0,1.3fr)' },
    { label: 'Find it by', key: 'ping.lookup' },
  ],
  'stop-contacting-me': [
    { label: 'They asked us to', key: 'suppression.action', track: 'minmax(0,1.4fr)' },
    { label: 'Also remove', key: 'suppression.other_contact' },
  ],
  'how-to-reach-me': [
    { label: 'Reach them by', key: 'pref.channel', track: 'minmax(0,0.7fr)' },
    { label: 'When', key: 'pref.days', track: 'minmax(0,0.7fr)' },
    { label: 'Language', key: 'pref.language', track: 'minmax(0,0.7fr)' },
    { label: 'Family OK', key: 'pref.family_ok', track: 'minmax(90px,0.5fr)' },
  ],
  // Enough to route and schedule the request, and no more. Mobility and the
  // free-text note are health-adjacent (07 §6) and stay in the detail drawer
  // rather than sitting in a list anyone scanning the queue can read.
  'access-interpreter': [
    { label: 'Visit date', key: 'visit.date', track: 'minmax(0,0.8fr)' },
    { label: 'Language', key: 'access.language', track: 'minmax(0,1.2fr)' },
  ],
  // The programme director is a named third party; they stay in the drawer.
  'resident-rotation': [
    { label: 'Program', key: 'training.program', track: 'minmax(0,1.5fr)' },
    { label: 'PGY', key: 'training.pgy', track: 'minmax(60px,0.4fr)' },
    { label: 'Dates', key: 'training.dates' },
  ],
  'student-observer': [
    { label: 'School', key: 'person.org', track: 'minmax(0,1.4fr)' },
    { label: 'Year', key: 'training.year', track: 'minmax(60px,0.5fr)' },
    { label: 'Asking about', key: 'training.ask' },
  ],
};

/** Who wrote — name over email — and the trailing received · due · status · actions. */
const LEAD = 'minmax(0,1.7fr)';
const TAIL = 'minmax(64px,0.6fr) minmax(96px,0.8fr) minmax(130px,1fr) 64px';
/** The upstream Form · About pair, used when no single form is chosen. */
const GENERIC = 'minmax(120px,1.2fr) minmax(0,2fr)';

export const columnsFor = (form: string): Column[] | null => FORM_COLUMNS[form] ?? null;

export const gridFor = (cols: Column[] | null): string =>
  `${LEAD} ${cols ? cols.map((c) => c.track ?? 'minmax(0,1fr)').join(' ') : GENERIC} ${TAIL}`;

/** Below this the card scrolls sideways rather than crushing the cells. */
export const minWidthFor = (cols: Column[] | null): number => 660 + (cols?.length ?? 2) * 130;

/** One cell, as the words the form showed; a dash when the answer is missing. */
export function cellOf(r: SubmissionRow, col: Column, L: Lookup): string {
  const fields = registryFieldsOf(r);
  return fields ? registryValue(col.key, fields[col.key], L, { short: true }) : '—';
}
